import { reactive } from 'vue'

export type ResourceType = 'json' | 'image' | 'video' | 'other'

export interface ResourceItem {
  id: string
  name: string
  type: ResourceType
  size: string
  url?: string
}

const STORAGE_KEY = 'mflow_resources'

export const resourceState = reactive({
  resources: [] as ResourceItem[]
})

function save() {
  try {
    const data = resourceState.resources.map(r => ({ ...r, url: undefined }))
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch (e) {
    console.warn('[resource.store] Failed to save', e)
  }
}

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const data = JSON.parse(raw)
      resourceState.resources.splice(0, resourceState.resources.length, ...data)
    }
  } catch (e) {
    console.warn('[resource.store] Failed to load', e)
  }
}

export function addResource(item: Omit<ResourceItem, 'id'>): ResourceItem {
  const id = `res_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
  const resource: ResourceItem = { id, ...item }
  resourceState.resources.push(resource)
  save()
  return resource
}

export function addResourceFromFile(file: File): ResourceItem {
  const size = formatFileSize(file.size)
  const type = detectType(file.name)
  const url = URL.createObjectURL(file)
  return addResource({ name: file.name, size, type, url })
}

export function deleteResource(id: string): void {
  const idx = resourceState.resources.findIndex(r => r.id === id)
  if (idx >= 0) {
    const [r] = resourceState.resources.splice(idx, 1)
    if (r?.url) URL.revokeObjectURL(r.url)
    save()
  }
}

export function getResources(filter: ResourceType | 'all' = 'all'): ResourceItem[] {
  if (filter === 'all') return resourceState.resources
  return resourceState.resources.filter(r => r.type === filter)
}

export function createJsonResource(name = 'data.json'): ResourceItem {
  return addResource({ name, type: 'json', size: '0KB' })
}

// 新增：资源导入/导出能力（不包含 url）
export interface ResourcesSnapshot {
  resources: Omit<ResourceItem, 'url'>[]
}

export function exportResources(): ResourcesSnapshot {
  return {
    resources: resourceState.resources.map(({ url, ...rest }) => ({ ...rest }))
  }
}

export function importResources(snapshot: ResourcesSnapshot): void {
  const list = (snapshot?.resources ?? []).map(r => ({ ...r }))
  resourceState.resources.splice(0, resourceState.resources.length, ...list)
  save()
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0KB'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + sizes[i]
}

function detectType(filename: string): ResourceType {
  const lower = filename.toLowerCase()
  if (lower.endsWith('.json')) return 'json'
  if (/(png|jpg|jpeg|gif|svg)$/.test(lower)) return 'image'
  if (/(mp4|webm|ogg)$/.test(lower)) return 'video'
  return 'other'
}

load()